import { useCallback } from "react"
import { ICliente, IClienteParams, IClienteResponse, IClienteTable, IGetClientesVendedorVelesResult, IProdutoTipoConsumoParams } from "../../application/models/ICliente"
import { IProdutoConsumo } from "../../application/models/IPerfil"
import { delay } from "../../utils/delay"
import { useAxiosInstances } from "../axios-instance"
import { IFaturamentoConfiguracao } from "../../application/models/IFaturamento"
import { IPlanoMultiRangeRequest } from "../../application/models"

export const useClienteApi = () => {
  const { axiosAuthInstance } = useAxiosInstances()

    const getClientesListApi = useCallback(async (data: {
      IdCliente: number
      IdUsuario: number
      RazaoSocial?: string | undefined
      NomeFantasia?: string | undefined
      LoginCliente?: string | undefined
      CNPJ?: string | undefined
      InclusaoMaxima?: Date | undefined
      InclusaoMinima?: Date | undefined
      IdVendedor?: number | undefined
      page: number
      limit: number
    }): Promise<{
      clientes: IClienteTable[],
      clientesPagination: {
        page: number,
        limit: number
      }
    }> => {
      try {

        const result = await axiosAuthInstance.get('/cliente/clientes', {
          params: {
            IdCliente: data.IdCliente,
            RazaoSocial: data.RazaoSocial,
            NomeFantasia: data.NomeFantasia,
            LoginCliente: data.LoginCliente,
            CNPJ: data.CNPJ,
            IdVendedor: data.IdVendedor,
            ...(data.InclusaoMaxima != null && { InclusaoMaxima: data.InclusaoMaxima }),
            ...(data.InclusaoMinima != null && { InclusaoMinima: data.InclusaoMinima }),
            IdUsuario: data.IdUsuario,
            page: data.page,
            limit: data.limit
          }
        })

        let clientes: IClienteTable[] = result.data.clientes.map((record: any) => ({
          IdCliente: record.IdCliente,
          RazaoSocial: record.RazaoSocial,
          NomeFantasia: record.NomeFantasia,
          LoginCliente: record.LoginCliente,
          CNPJ: record.CNPJ,
          Ativo: record.Ativo,
          Inclusao: record.Inclusao,
          Atualizacao: record.Atualizacao,
          IdVendedor: record.IdVendedor,
          LoginUsuarioVendedor: record.LoginUsuarioVendedor,
          TotalLinhas: record.TotalLinhas,
          GerarLoteFinanceiro: record.GerarLoteFinanceiro ?? false
        }))

        return {
          clientes,
          clientesPagination: {
            page: data.page,
            limit: data.limit
          }
        }
      } catch (error: any) {
        // Tratar erro 500 silenciosamente para não estourar no console
        if (error?.response?.status === 500) {
          // Retornar valor padrão em caso de erro 500
          return {
            clientes: [],
            clientesPagination: {
              page: data.page,
              limit: data.limit
            }
          }
        }
        // Para outros erros, propagar normalmente
        throw error;
      }
    }, [axiosAuthInstance])
    // cadastrar cliente 

    const createClienteApi = useCallback(async (data: {
      cnpj: string,
      razaoSocial: string,
      nomeFantasia?: string,
      login: string,
      idVendedor: number,
      ListaProdutosTiposConsumos: {
        IdProduto: number
        IdTipoConsumo: number[]
      }[],
      plano: IPlanoMultiRangeRequest,
      faturamento?: IFaturamentoConfiguracao | null,
      GerarLoteFinanceiro?: boolean

    }): Promise<ICliente> => {
      try {

        // na chamada do axios, os parâmetros deve estar EXATAMENTE igual aos parâmetros recebidos na classe Controller no backend   
        const result = await axiosAuthInstance.post('/cliente', {
          CNPJ: data.cnpj,
          RazaoSocial: data.razaoSocial,
          NomeFantasia: data.nomeFantasia,
          LoginCliente: data.login,
          idVendedor: data.idVendedor,
          ProdutosTiposConsumos: data.ListaProdutosTiposConsumos,
          plano: data.plano,
          faturamento: data.faturamento,
          GerarLoteFinanceiro: data.GerarLoteFinanceiro ?? false
        })

        return result.data
      } catch (error) {

        throw error
      }
    }, [axiosAuthInstance])

    const updateClienteApi = useCallback(async (data: {
      cliente: IClienteParams,
      produtosTipoConsumos: IProdutoTipoConsumoParams[],
      plano?: IPlanoMultiRangeRequest,
      faturamento?: {
        Ativo?: boolean
        NotaFiscalModeloDescricaoPersonalizado?: string
        DiaVencimento?: number
        SimplesNacional?: boolean
        IdNotaFiscalModeloDescricao?: number
      } | null
    }): Promise<IClienteResponse> => {
      // Transformar a estrutura dos ProdutosTiposConsumos
      const produtosTiposConsumosTransformados = data.produtosTipoConsumos.flatMap(item => {
        // Se IdTipoConsumo for um array, criar um registro para cada valor
        if (Array.isArray(item.IdTipoConsumo)) {
          return item.IdTipoConsumo.map(tipoConsumo => ({
            IdProduto: Number(item.IdProduto),
            IdTipoConsumo: Number(tipoConsumo)
          }));
        }
        // Se não for array, manter como está
        return [{
          IdProduto: Number(item.IdProduto),
          IdTipoConsumo: Number(item.IdTipoConsumo)
        }];
      });

      const requestData = {
        IdCliente: data.cliente.IdCliente,
        RazaoSocial: data.cliente.RazaoSocial,
        NomeFantasia: data.cliente.NomeFantasia,
        CNPJ: data.cliente.CNPJ,
        LoginCliente: data.cliente.LoginCliente,
        IdVendedor: Number(data.cliente.IdVendedor),
        Ativo: data.cliente.Ativo,
        ProdutosTiposConsumos: produtosTiposConsumosTransformados,
        plano: data.plano,
        faturamento: data.faturamento,
        GerarLoteFinanceiro: data.cliente.GerarLoteFinanceiro ?? false
      };


      const result = await axiosAuthInstance.put('/cliente', requestData);
      return result.data
    }, [axiosAuthInstance])

    const getClienteInfoByCnpjApi = useCallback(async (data: {
      cnpj: string
    }): Promise<{
      razaoSocial: string,
      nomeFantasia: string
    }> => {
      try {
        const result = await axiosAuthInstance.get('/cliente/buscar-empresa-externa', {
          params: {
            CNPJ: data.cnpj
          }
        })

        return {
          razaoSocial: result.data.RazaoSocial,
          nomeFantasia: result.data.NomeFantasia
        }
      } catch (error: any) {
        if (error.response?.data?.error) {
          throw new Error(error.response.data.error)
        }
        throw new Error('Erro ao buscar empresa externa por CNPJ')
      }
    }, [axiosAuthInstance])

    const getIpsVinculadosByClienteIdApi = useCallback(async (id: string): Promise<string[]> => {
      await delay(1551)
      return [
        '245.215.180.40',
        '150.85.33.1',
        '242.235.151.34',
        '254.105.183.53',
        '56.223.202.104'
      ]
    }, [])

    const updateIpsVinculadosByClienteIdApi = useCallback(async (data: {
      id: string,
      ips: string[]
    }): Promise<string[]> => {
      await delay(1551)
      return data.ips
    }, [])

    const getProdutoConsumoVinculadoByClienteIdApi = useCallback(async (id: string): Promise<IProdutoConsumo[]> => {
      // Busca a lista de todos os produtos e tipos de consumo disponíveis
      const allProdutosResponse = await axiosAuthInstance.get('/produto/produtoTipoConsumo');
      const allProdutos = allProdutosResponse.data.produtoClienteTipoConsumo || [];

      // Busca os vínculos atuais do cliente
      const vinculadosResponse = await axiosAuthInstance.get('/produtoCliente/produtosCliente', {
        params: { IdCliente: id }
      });
      const vinculados = vinculadosResponse.data.produtosTipoConsumo || [];

      // Mapeia para a estrutura IProdutoConsumo[] que o frontend espera
      return allProdutos.map((item: any) => {
        const isProdutoVinculado = vinculados.some((v: any) => v.IdProduto === item.produto.IdProduto);
        
        return {
          produtoId: String(item.produto.IdProduto),
          produto: item.produto.Descricao,
          status: isProdutoVinculado,
          limit: 0,
          consumos: item.tipoConsumo.map((tc: any) => ({
            tipoConsumoId: String(tc.IdTipoConsumo),
            nome: tc.Descricao,
            status: vinculados.some((v: any) => v.IdProduto === item.produto.IdProduto && v.IdTipoConsumo === tc.IdTipoConsumo)
          }))
        };
      });
    }, [axiosAuthInstance])

    const updateProdutoConsumoVinculadoByClienteIdApi = useCallback(async (data: {
      clienteId: string,
      produtos: IProdutoConsumo[]
    }): Promise<IProdutoConsumo[]> => {
      // Simulação de sucesso mantida, pois o Edit form usa updateClienteApi diretamente
      await delay(1000);
      return data.produtos;
    }, [])

    const uploadContratoApi = useCallback(async (data: {
      IdCliente: number,
      file: File
    }): Promise<any> => {
      try {
        const formData = new FormData()
        formData.append('file', data.file)
        
        const result = await axiosAuthInstance.post(`/cliente/${data.IdCliente}/contrato`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        return result.data
      } catch (error) {
        throw error
      }
    }, [axiosAuthInstance])

    const getContratoApi = useCallback(async (data: {
      IdCliente: number
    }): Promise<any> => {
      try {
        const result = await axiosAuthInstance.get(`/cliente/${data.IdCliente}/contrato`)
        return result.data
      } catch (error) {
        // Retorna null caso não tenha contrato
        return null
      }
    }, [axiosAuthInstance])

    const getClientesVendedorVelesApi = useCallback(async (idUsuario?: number): Promise<IGetClientesVendedorVelesResult> => {
      try {
        const result = await axiosAuthInstance.get('/cliente/vendedor-veles', {
          params: idUsuario
        })
        return result.data
      } catch (error) {
        throw error
      }
    }, [axiosAuthInstance])

    return {
      //getClienteByIdApi,
      createClienteApi,
      updateClienteApi,
      getClienteInfoByCnpjApi,
      getIpsVinculadosByClienteIdApi,
      updateIpsVinculadosByClienteIdApi,
      getProdutoConsumoVinculadoByClienteIdApi,
      updateProdutoConsumoVinculadoByClienteIdApi,
      getClientesListApi,
      uploadContratoApi,
      getContratoApi,
      getClientesVendedorVelesApi
      //getClientesFilterApi
    }
  }